// Shared utility functions for task handling
import {TextSelection} from 'prosemirror-state';

export const COMPLIANCE_STANDARDS = {
    AJA: {id: 'AJA', title: 'Make Accurate Judgments and Assessments'},
    COT: {id: 'COT', title: 'Consider and Explain Change Over Time'},
    AU: {id: 'AU', title: 'Address Uncertainty'},
    ULA: {id: 'ULA', title: 'Use Logical Argumentation'},
    IJI: {id: 'IJI', title: 'Distinguish Between Intelligence Judgments and Information'},
    AVJ: {id: 'AVJ', title: 'Distinguish Between Assumptions and Judgments'},
    QRS: {id: 'QRS', title: 'Properly Describe Quality and Reliability of Sources'},
};

export const BEST_PRACTICES = {
    'BP-PUR': {id: 'BP-PUR', title: 'Explicit Purpose and Decision Relevance'},
    'BP-BLUF': {id: 'BP-BLUF', title: 'Bottom Line Up Front (BLUF)'},
    'BP-JUDG': {id: 'BP-JUDG', title: 'Clear, Testable Analytic Judgments'},
    'BP-REAS': {id: 'BP-REAS', title: 'Transparent Reasoning Chain'},
    'BP-EVID': {id: 'BP-EVID', title: 'Relevant, Sufficient, and Weighted Evidence'},
    'BP-ALT': {id: 'BP-ALT', title: 'Consideration of Alternatives and Competing Hypotheses'},
    'BP-UNC': {id: 'BP-UNC', title: 'Explicit Assumptions and Uncertainties'},
    'BP-CONF': {id: 'BP-CONF', title: 'Calibrated and Meaningful Confidence'},
    'BP-STR': {id: 'BP-STR', title: 'Structure That Serves the Analysis'},
    'BP-WRIT': {id: 'BP-WRIT', title: 'Clear, Direct, Analyst-Invisible Writing'},
};

/**
 * Get standard title by ID
 * @param {string} standardId - The standard ID (e.g., 'IJI', 'QRS')
 * @returns {string} The standard title or the ID if not found
 */
export function getStandardTitle(standardId) {
    return COMPLIANCE_STANDARDS[standardId]?.title || standardId;
}

/**
 * Get best practice title by ID
 * @param {string} bpId - The best practice ID (e.g., 'BP-PUR', 'BP-BLUF')
 * @returns {string} The best practice title or the ID if not found
 */
export function getBPTitle(bpId) {
    return BEST_PRACTICES[bpId]?.title || bpId;
}

/**
 * Calculate severity based on the number of non-compliant standards
 * @param {Object} task - The task object with standardIds
 * @returns {'minor'|'moderate'|'major'|'critical'} Severity level
 */
export function getComplianceSeverity(task) {
    const count = task?.standardIds?.length || (task?.standardId ? 1 : 0);
    if (count >= 5) return 'critical';
    if (count >= 3) return 'major';
    if (count >= 2) return 'moderate';
    return 'minor';
}

/**
 * Generate a self-explanatory title based on task type and data
 * @param {Object} task - The task object
 * @returns {string} A human-readable task title
 */
export function getTaskTitle(task) {
    const taskType = task?.taskType;

    if (taskType === 'entity_recognition') {
        const entityType = task.entityType || 'Entity';
        return `Link ${entityType.toLowerCase()}`;
    }

    if (taskType === 'compliance_check') {
        // Show count of non-compliant standards out of total
        const totalStandards = Object.keys(COMPLIANCE_STANDARDS).length;
        const nonCompliantCount = task.standardIds?.length || 0;

        if (nonCompliantCount > 0) {
            return `${nonCompliantCount}/${totalStandards} standards`;
        }

        // Fallback to single standardId if present
        if (task.standardId && COMPLIANCE_STANDARDS[task.standardId]) {
            return `1/${totalStandards} standards`;
        }

        // Fallback to improvementType if standardId not found
        const improvementType = task.improvementType;
        if (improvementType === 'rewrite') {
            return 'Rewrite passage';
        }
        if (improvementType === 'clarify') {
            return 'Clarify text';
        }
        if (improvementType === 'shorten') {
            return 'Shorten text';
        }
        if (improvementType === 'expand') {
            return 'Expand text';
        }
        if (improvementType) {
            return `${improvementType.charAt(0).toUpperCase()}${improvementType.slice(1)} text`;
        }
        return 'Improve compliance';
    }

    if (taskType === 'best_practices_check') {
        // Show count of non-compliant best practices out of total
        const totalPractices = Object.keys(BEST_PRACTICES).length;
        const nonCompliantCount = task.standardIds?.length || 0;

        if (nonCompliantCount > 0) {
            return `${nonCompliantCount}/${totalPractices} practices`;
        }

        // Fallback to single standardId if present
        if (task.standardId && BEST_PRACTICES[task.standardId]) {
            return `1/${totalPractices} practices`;
        }

        return 'Improve practices';
    }

    // Default fallback
    return 'Task';
}

/**
 * Get a short description for a task type
 * @param {Object} task - The task object
 * @returns {string} A short description
 */
export function getTaskDescription(task) {
    const taskType = task?.taskType;

    if (taskType === 'entity_recognition') {
        return 'Link this entity to a reference';
    }

    if (taskType === 'compliance_check') {
        return 'Improve text compliance';
    }

    if (taskType === 'best_practices_check') {
        return 'Improve best practices adherence';
    }

    return 'Complete this task';
}

/**
 * Insert text from a task into the document
 * This function programmatically replaces text while preserving marks
 * @param {Object} editorView - The ProseMirror editor view
 * @param {Object} task - The task object containing suggestedText, blockId, and range
 * @returns {Promise<void>}
 */
export async function insertText(editorView, task) {
    if (!editorView || !task?.suggestedText || !task?.blockId || !Array.isArray(task?.range)) {
        console.warn('insertText: missing required parameters', {editorView, task});
        return;
    }

    const {state} = editorView;
    const {doc} = state;

    // Find the block by blockId
    let blockPos = null;
    let blockNode = null;

    doc.descendants((node, pos) => {
        if (node.attrs.blockId === task.blockId) {
            blockPos = pos;
            blockNode = node;
            return false; // Stop iteration
        }
    });

    if (!blockNode || blockPos === null) {
        console.warn('insertText: block not found', {blockId: task.blockId});
        return;
    }

    const flagged = typeof task.originalText === 'string' ? task.originalText : '';
    if (flagged && !(blockNode.textContent || '').includes(flagged)) {
        console.warn('insertText: flagged text is no longer in the block', {blockId: task.blockId});
        return;
    }

    // Build the updated node data programmatically
    const [rangeStart, rangeEnd] = task.range;
    const updatedNodeData = buildUpdatedNodeWithMarks(blockNode, rangeStart, rangeEnd, task.suggestedText);

    console.log('insertText: Built updated node programmatically', updatedNodeData);

    // Replace the node with the updated version
    // Pass the range info so we can calculate where to place the cursor
    replaceNodeWithUpdatedContent(editorView, blockPos, updatedNodeData, rangeStart, task.suggestedText.length);
}

/**
 * Build an updated node by replacing text in a range while preserving marks
 * @param {Object} blockNode - The ProseMirror block node
 * @param {number} rangeStart - Start of the range to replace (character offset)
 * @param {number} rangeEnd - End of the range to replace (character offset)
 * @param {string} newText - The new text to insert
 * @returns {Object} Updated node data structure
 */
function buildUpdatedNodeWithMarks(blockNode, rangeStart, rangeEnd, newText) {
    // Step 1: Extract all text segments with their marks and character positions
    const segments = [];
    let currentPos = 0;

    blockNode.forEach((child) => {
        if (child.isText) {
            const text = child.text;
            const marks = child.marks.map(mark => ({
                type: mark.type.name,
                attrs: mark.attrs
            }));

            segments.push({
                start: currentPos,
                end: currentPos + text.length,
                text: text,
                marks: marks
            });

            currentPos += text.length;
        }
    });

    // Step 2: Build the new text by combining:
    // - Text before rangeStart
    // - The new text
    // - Text after rangeEnd
    const originalFullText = segments.map(s => s.text).join('');
    const beforeRange = originalFullText.substring(0, rangeStart);
    const afterRange = originalFullText.substring(rangeEnd);
    const newFullText = beforeRange + newText + afterRange;

    // Step 3: Calculate how positions shift
    const lengthDiff = newText.length - (rangeEnd - rangeStart);

    // Step 4: Build mark ranges - each mark type+attrs combo mapped to character ranges
    const markRanges = [];
    for (const segment of segments) {
        for (const mark of segment.marks) {
            // Find or create a mark range entry
            const markKey = JSON.stringify(mark);
            let markRange = markRanges.find(mr => JSON.stringify(mr.mark) === markKey);

            if (!markRange) {
                markRange = {
                    mark: mark,
                    ranges: []
                };
                markRanges.push(markRange);
            }

            // Add this segment's range
            markRange.ranges.push({start: segment.start, end: segment.end});
        }
    }

    // Merge adjacent ranges for each mark
    for (const markRange of markRanges) {
        markRange.ranges.sort((a, b) => a.start - b.start);
        const merged = [];
        for (const range of markRange.ranges) {
            if (merged.length === 0 || merged[merged.length - 1].end < range.start) {
                merged.push({...range});
            } else {
                merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, range.end);
            }
        }
        markRange.ranges = merged;
    }

    // Step 5: Adjust mark ranges for the text replacement
    const adjustedMarkRanges = markRanges.map(mr => {
        const adjustedRanges = [];

        for (const range of mr.ranges) {
            let newStart = range.start;
            let newEnd = range.end;

            // If range is entirely before the replacement, no change
            if (range.end <= rangeStart) {
                adjustedRanges.push({start: newStart, end: newEnd});
                continue;
            }

            // If range is entirely after the replacement, shift by lengthDiff
            if (range.start >= rangeEnd) {
                adjustedRanges.push({
                    start: newStart + lengthDiff,
                    end: newEnd + lengthDiff
                });
                continue;
            }

            // Range overlaps with replacement - need to handle carefully
            // Part before replacement
            if (range.start < rangeStart) {
                newStart = range.start;
            } else {
                newStart = rangeStart;
            }

            // Part after replacement
            if (range.end > rangeEnd) {
                newEnd = range.end + lengthDiff;
            } else {
                newEnd = rangeStart + newText.length;
            }

            // Only add if range is valid
            if (newEnd > newStart && newEnd <= newFullText.length) {
                adjustedRanges.push({start: newStart, end: newEnd});
            }
        }

        return {
            mark: mr.mark,
            ranges: adjustedRanges
        };
    }).filter(mr => mr.ranges.length > 0);

    // Step 6: Build character-to-marks mapping for new text
    const charMarks = new Array(newFullText.length).fill(null).map(() => []);

    for (const mr of adjustedMarkRanges) {
        for (const range of mr.ranges) {
            for (let i = range.start; i < range.end && i < newFullText.length; i++) {
                const markKey = JSON.stringify(mr.mark);
                if (!charMarks[i].some(m => JSON.stringify(m) === markKey)) {
                    charMarks[i].push(mr.mark);
                }
            }
        }
    }

    // Step 7: Build content array by grouping consecutive characters with same marks
    const content = [];
    let currentText = '';
    let currentMarks = null;

    for (let i = 0; i < newFullText.length; i++) {
        const char = newFullText[i];
        const marks = charMarks[i];
        const marksKey = JSON.stringify(marks);

        if (currentMarks === null) {
            currentMarks = marks;
            currentText = char;
        } else if (JSON.stringify(currentMarks) === marksKey) {
            currentText += char;
        } else {
            // Push current segment
            content.push({
                type: 'text',
                text: currentText,
                marks: currentMarks
            });
            currentMarks = marks;
            currentText = char;
        }
    }

    // Push last segment
    if (currentText) {
        content.push({
            type: 'text',
            text: currentText,
            marks: currentMarks || []
        });
    }

    // Step 8: Return the updated node structure
    return {
        type: blockNode.type.name,
        attrs: blockNode.attrs,
        content: content,
        marks: blockNode.marks.map(mark => ({
            type: mark.type.name,
            attrs: mark.attrs
        }))
    };
}

/**
 * Replace a node in the document with updated content from LLM
 * @param {Object} editorView - The ProseMirror editor view
 * @param {number} blockPos - Position of the block node
 * @param {Object} updatedNodeData - Updated node data from LLM with preserved marks
 * @param {number} rangeStart - Character offset where the replacement started (optional)
 * @param {number} insertedTextLength - Length of the inserted text (optional)
 */
export function replaceNodeWithUpdatedContent(editorView, blockPos, updatedNodeData, rangeStart = null, insertedTextLength = null) {
    const {state, dispatch} = editorView;
    const {schema, doc} = state;

    // Find the original node
    const originalNode = doc.nodeAt(blockPos);
    if (!originalNode) {
        console.warn('replaceNodeWithUpdatedContent: node not found at position', blockPos);
        return;
    }

    // Recreate the node from the updated data
    try {
        const nodeType = schema.nodes[updatedNodeData.type];
        if (!nodeType) {
            console.warn('replaceNodeWithUpdatedContent: node type not found', updatedNodeData.type);
            return;
        }

        // Recreate child nodes with marks
        const content = updatedNodeData.content.map(childData => {
            if (childData.type === 'text') {
                const marks = childData.marks.map(markData => {
                    const markType = schema.marks[markData.type];
                    return markType ? markType.create(markData.attrs) : null;
                }).filter(Boolean);

                return schema.text(childData.text, marks);
            } else {
                // Handle other node types if needed
                const childType = schema.nodes[childData.type];
                return childType ? childType.create(childData.attrs) : null;
            }
        }).filter(Boolean);

        // Create the updated node
        const updatedNode = nodeType.create(
            updatedNodeData.attrs,
            content,
            updatedNodeData.marks?.map(markData => {
                const markType = schema.marks[markData.type];
                return markType ? markType.create(markData.attrs) : null;
            }).filter(Boolean)
        );

        // Replace the node in the document
        const tr = state.tr.replaceWith(
            blockPos,
            blockPos + originalNode.nodeSize,
            updatedNode
        );

        dispatch(tr);

        // Calculate cursor position: if we have range info, place cursor right after inserted text
        // Otherwise, place it at the end of the block
        let calculatedCursorPos;
        if (rangeStart !== null && insertedTextLength !== null) {
            // Cursor should be at: blockPos (block start) + 1 (opening tag) + rangeStart (offset to replacement) + insertedTextLength (length of new text)
            calculatedCursorPos = blockPos + 1 + rangeStart + insertedTextLength;
        } else {
            // Fallback to end of block
            calculatedCursorPos = blockPos + 1 + updatedNode.content.size;
        }

        console.log('replaceNodeWithUpdatedContent: Dispatched replacement transaction', {
            blockPos,
            rangeStart,
            insertedTextLength,
            originalNodeSize: originalNode.nodeSize,
            updatedNodeContentSize: updatedNode.content.size,
            calculatedCursorPos
        });

        // Use setTimeout with a longer delay to ensure all React updates are complete
        setTimeout(() => {
            try {
                // Focus editor first
                editorView.focus();

                const newState = editorView.state;

                console.log('replaceNodeWithUpdatedContent: About to set cursor', {
                    cursorPos: calculatedCursorPos,
                    docSize: newState.doc.content.size,
                    currentSelection: {from: newState.selection.from, to: newState.selection.to}
                });

                // Create a collapsed selection (cursor only) at the calculated position
                const selection = TextSelection.create(newState.doc, calculatedCursorPos, calculatedCursorPos);

                console.log('replaceNodeWithUpdatedContent: Created selection', {
                    from: selection.from,
                    to: selection.to,
                    empty: selection.empty,
                    anchor: selection.anchor,
                    head: selection.head
                });

                const trFocus = newState.tr.setSelection(selection);
                trFocus.scrollIntoView();
                editorView.dispatch(trFocus);

                console.log('replaceNodeWithUpdatedContent: Cursor positioned successfully');
            } catch (error) {
                console.error('replaceNodeWithUpdatedContent: Error setting cursor', error);
            }
        }, 100);
    } catch (error) {
        console.error('replaceNodeWithUpdatedContent: Error creating updated node', error);
    }
}

